import {cn} from '@tempmee/core'
import {SolidXCircleIcon} from '@tempmee/react-icons'
import {
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  forwardRef,
  isValidElement,
} from 'react'
import {chipVariants} from './Variants'

export type ChipVariant = 'filled' | 'outlined'
export type ChipColor = 'primary' | 'secondary'
export type ChipSize = 'small' | 'medium'

export interface ChipProps extends Omit<HTMLAttributes<HTMLDivElement>, 'color' | 'onClick'> {
  /** Chip text (or any node). */
  label: ReactNode
  /** Filled (solid/tinted) or outlined. @default 'filled' */
  variant?: ChipVariant
  /** `secondary` = unselected (default), `primary` = selected/active. @default 'secondary' */
  color?: ChipColor
  /** @default 'medium' */
  size?: ChipSize
  /** Leading icon (22px medium / 16px small). */
  icon?: ReactNode
  /** Makes the chip clickable (adds hover/focus/pressed states + button semantics). */
  onClick?: () => void
  /** Renders a trailing delete button (circular ×) and fires on click/Backspace. */
  onDelete?: () => void
  /** Override the default delete glyph. */
  deleteIcon?: ReactNode
  /** Dim + block interaction. */
  disabled?: boolean
}

// Size-keyed internal metrics (the Figma nests padding: container + label/icon).
const SIZE = {
  medium: {
    label: 'px-1 text-[16px] leading-[1.2] tracking-[0.15px]',
    icon: 'pl-1 [&_svg]:size-[22px]',
    delete: 'size-6',
  },
  small: {
    label: 'px-1 text-[14px] leading-[1.43] tracking-[0.17px]',
    icon: 'pl-0.5 [&_svg]:size-4',
    delete: 'size-4',
  },
} as const

/**
 * MUI-aligned `Chip` — a compact pill for an input, attribute, or action. `color`
 * carries selection (`primary` = selected, `secondary` = default). Clickable chips
 * render `<div role="button">` so a trailing delete `<button>` can nest validly.
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      label,
      variant = 'filled',
      color = 'secondary',
      size = 'medium',
      icon,
      onClick,
      onDelete,
      deleteIcon,
      disabled = false,
      className,
      ...props
    },
    ref,
  ) => {
    const clickable = !!onClick
    const metrics = SIZE[size]

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (clickable && !disabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        onClick?.()
      }
      // Backspace/Delete removes the chip when deletable (MUI behavior).
      if (onDelete && !disabled && (e.key === 'Backspace' || e.key === 'Delete')) {
        onDelete()
      }
      props.onKeyDown?.(e)
    }

    return (
      <div
        ref={ref}
        className={cn(chipVariants({variant, color, size, clickable, disabled}), className)}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable && !disabled ? 0 : undefined}
        aria-disabled={disabled || undefined}
        onClick={clickable && !disabled ? onClick : undefined}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {icon != null && (
          <span className={cn('flex shrink-0 items-center', metrics.icon)}>{icon}</span>
        )}
        <span
          className={cn(
            'flex min-w-6 items-center justify-center font-sans font-normal',
            metrics.label,
          )}
        >
          {label}
        </span>
        {onDelete && (
          <button
            type="button"
            aria-label="Delete"
            tabIndex={-1}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) onDelete()
            }}
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full text-current transition-opacity [&_svg]:size-full',
              metrics.delete,
              variant === 'filled' ? 'opacity-70 hover:opacity-100' : 'hover:opacity-70',
            )}
          >
            {deleteIcon && isValidElement(deleteIcon) ? deleteIcon : <SolidXCircleIcon />}
          </button>
        )}
      </div>
    )
  },
)
Chip.displayName = 'Chip'
